/**
 * The lease card's "Live Updates…" ticker.
 *
 * The nightly leasing job writes a lease's reward for a UTC day ONCE, at 00:00
 * UTC, and `accumulated_leasing_rewards` includes that row from then on. Showing
 * the whole day's amount at midnight would make the number jump; this hook
 * instead reveals it linearly across its own UTC day:
 *
 *   shown = accumulated - lastDay + lastDay * (time since 00:00 UTC of that day / 24h)
 *
 * clamped so it never exceeds `accumulated`. `lastDay` / `lastDate` are the
 * lease's real latest `daily_leasing_distributions` row, sent by the API as
 * `last_daily_leasing_reward` / `last_distribution_date`.
 *
 * Two bugs this replaced, both from the Next.js original:
 *   - the daily amount was `accumulated * 0.02`, a guess that grows with lease
 *     age (staging lease 39: 5,840/day shown vs 589.61 actually paid);
 *   - the day ran from LOCAL midnight, so outside UTC the value stepped backwards
 *     once a day.
 * Progress is also measured from the ROW's date, not "today", so a row that is
 * a day old (job not run yet, or the page left open past midnight) reads as
 * fully revealed instead of restarting from zero.
 */
import { useEffect, useState } from "react";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Re-render cadence. The value is recomputed from the clock, not accumulated. */
const TICK_MS = 5000;

interface UseDynamicRewardsProps {
  /** `accumulated_leasing_rewards`: settled total, INCLUDING the latest row. */
  totalAccumulatedRewards: number;
  /** `last_daily_leasing_reward`. Null/0 disables the ticker. */
  lastDayRewards?: number | null;
  /** `last_distribution_date`, `YYYY-MM-DD` (a UTC day). */
  lastDistributionDate?: string | null;
  /** False once the lease stops earning; the settled total is shown as-is. */
  isActive?: boolean;
}

interface DynamicRewardsReturn {
  displayRewards: number;
  isIncrementing: boolean;
}

/**
 * The ticker value at `now`. Pure, so the arithmetic is checkable without React.
 * Returns the settled total whenever there is nothing to reveal.
 */
export const liveLeaseRewardAt = (
  total: number,
  lastDay: number | null | undefined,
  lastDate: string | null | undefined,
  now: number,
): number => {
  if (!lastDay || lastDay <= 0 || !lastDate) return total;

  const dayStart = Date.parse(`${lastDate}T00:00:00Z`);

  if (Number.isNaN(dayStart)) return total;

  const progress = Math.min(Math.max((now - dayStart) / MS_PER_DAY, 0), 1);

  // `lastDay` can never exceed `total` in real data; the floor keeps a corrupt
  // row from rendering a negative balance.
  return Math.min(total, Math.max(0, total - lastDay) + lastDay * progress);
};

/** Live-updating reward figure for one lease card. */
export const useDynamicRewards = ({
  totalAccumulatedRewards,
  lastDayRewards,
  lastDistributionDate,
  isActive = true,
}: UseDynamicRewardsProps): DynamicRewardsReturn => {
  const total = Number(totalAccumulatedRewards);
  const lastDay = lastDayRewards == null ? null : Number(lastDayRewards);

  const compute = () =>
    isActive ? liveLeaseRewardAt(total, lastDay, lastDistributionDate, Date.now()) : total;

  const [displayRewards, setDisplayRewards] = useState(compute);
  const [isIncrementing, setIsIncrementing] = useState(false);

  useEffect(() => {
    setDisplayRewards(compute());

    if (!isActive || !lastDay || lastDay <= 0) return undefined;

    let flashTimer: ReturnType<typeof setTimeout> | undefined;

    const interval = setInterval(() => {
      const next = compute();

      setDisplayRewards((prev) => {
        // Flash only on a real increase, as the original did per increment.
        if (next > prev) {
          setIsIncrementing(true);
          clearTimeout(flashTimer);
          flashTimer = setTimeout(() => setIsIncrementing(false), 300);
        }

        return next;
      });
    }, TICK_MS);

    return () => {
      clearInterval(interval);
      clearTimeout(flashTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `compute` closes over exactly these
  }, [total, lastDay, lastDistributionDate, isActive]);

  return {
    displayRewards,
    isIncrementing,
  };
};
